import React from 'react';
import PropTypes from 'prop-types';
import { round1Display as r1d, ratioToPercentPlus } from '../../lib/format';
import { Link } from 'react-router-dom';

import { pathTo } from '../../App/routes';

const BOXSCORE_FIELDS = [
  'team',
  'gp',
  'min',
  'fg',
  'fg%',
  '3pt',
  '3p%',
  'ft',
  'ft%',
  'oreb',
  'dreb',
  'reb',
  'ast',
  'stl',
  'blk',
  'to',
  'pf',
  '+/-',
  'pts',
];

const AVERAGE_FIELDS = BOXSCORE_FIELDS.filter(
  field => !['+/-'].includes(field),
);

const SimpleBoxscoreRow = ({
  label,
  team,
  league,
  season,
  boxscore,
  total = false,
  games = 0,
}) => (
  <tr className={total ? 's-player-view__boxscore__career footer' : ''}>
    <td className="nan label double">{label}</td>
    <td className="nan team">
      <div>
        {!total && team && (
          <div>
            <img alt={`Team Logo - ${team.name}`} src={team.image} />
          </div>
        )}
        {league ? (
          <Link
            to={pathTo('team', league.id, season.id, team.id)}
            className="s-player-view__link"
          >
            {team.name_short}
          </Link>
        ) : (
          'Career'
        )}
      </div>
    </td>
    <td>{games}</td>
    <td>{Math.round(boxscore.minutes)}</td>
    <td>
      {boxscore.fgm}/{boxscore.fga}
    </td>
    <td>{ratioToPercentPlus(boxscore.fgm / boxscore.fga, 1)}</td>
    <td>
      {boxscore.fgm3}/{boxscore.fga3}
    </td>
    <td>{ratioToPercentPlus(boxscore.fgm3 / boxscore.fga3, 1)}</td>
    <td>
      {boxscore.ft}/{boxscore.fta}
    </td>
    <td>{ratioToPercentPlus(boxscore.ft / boxscore.fta, 1)}</td>
    <td>{boxscore.rebso}</td>
    <td>{boxscore.rebsd}</td>
    <td>{boxscore.rebs}</td>
    <td>{boxscore.ast}</td>
    <td>{boxscore.stl}</td>
    <td>{boxscore.blk}</td>
    <td>{boxscore.to}</td>
    <td>{boxscore.fouls}</td>
    <td>{boxscore.plus_minus}</td>
    <td>{boxscore.pts}</td>
  </tr>
);

const AverageBoxscoreRow = ({
  label,
  team,
  league,
  season,
  boxscore,
  total = false,
  games = 0,
}) => (
  <tr className={total ? 's-player-view__boxscore__career footer' : ''}>
    <td className="nan label double">{label}</td>
    <td className="nan team">
      <div>
        {!total && team && (
          <div>
            <img alt={`Team Logo - ${team.name}`} src={team.image} />
          </div>
        )}
        {league ? (
          <Link
            to={pathTo('team', league.id, season.id, team.id)}
            className="s-player-view__link"
          >
            {team.name_short}
          </Link>
        ) : (
          'Career'
        )}
      </div>
    </td>
    <td>{games}</td>
    <td>{r1d(boxscore.minutes / games)}</td>
    <td>
      {r1d(boxscore.fgm / games)}/{r1d(boxscore.fga / games)}
    </td>
    <td>{ratioToPercentPlus(boxscore.fgm / boxscore.fga, 1)}</td>
    <td>
      {r1d(boxscore.fgm3 / games)}/{r1d(boxscore.fga3 / games)}
    </td>
    <td>{ratioToPercentPlus(boxscore.fgm3 / boxscore.fga3, 1)}</td>
    <td>
      {r1d(boxscore.ft / games)}/{r1d(boxscore.fta / games)}
    </td>
    <td>{ratioToPercentPlus(boxscore.ft / boxscore.fta, 1)}</td>
    <td>{r1d(boxscore.rebso / games)}</td>
    <td>{r1d(boxscore.rebsd / games)}</td>
    <td>{r1d(boxscore.rebs / games)}</td>
    <td>{r1d(boxscore.ast / games)}</td>
    <td>{r1d(boxscore.stl / games)}</td>
    <td>{r1d(boxscore.blk / games)}</td>
    <td>{r1d(boxscore.to / games)}</td>
    <td>{r1d(boxscore.fouls / games)}</td>
    <td>{r1d(boxscore.pts / games)}</td>
  </tr>
);

const SeasonBoxscoreRow = ({
  games,
  season,
  league,
  stats,
  team,
  total = false,
  average = false,
}) => {
  const { boxscore } = stats.player;
  const label = season.name;
  let props = { label, boxscore, league, season, team, total, games };

  return average ? (
    <AverageBoxscoreRow {...props} />
  ) : (
    <SimpleBoxscoreRow {...props} />
  );
};

const SeasonBoxscores = ({ seasons, career, average = false }) => {
  const headers = average ? AVERAGE_FIELDS : BOXSCORE_FIELDS;

  return (
    <table className="s-player-view__boxscores collapse">
      <thead>
        <tr className="header">
          <th className="s-player-view__boxscores__season--name">Season</th>
          {headers.map(field => (
            <th key={field}>{field.toUpperCase()}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {seasons.map(season => (
          <SeasonBoxscoreRow
            key={`${season.season.id}-${season.team.id}`}
            {...season}
            average={average}
          />
        ))}
        <SeasonBoxscoreRow {...career} total={true} average={average} />
      </tbody>
    </table>
  );
};

const PlayerViewBoxscore = ({ player }) => {
  const { stats } = player;
  const career = stats && stats[stats.length - 1];
  const seasons =
    stats &&
    stats
      .filter(season => season.season.name.toLowerCase() !== 'career')
      .sort((a, b) => a.season.start_year - b.season.start_year);

  return (
    <div className="s-player-view__seasons">
      <div className="summary-section">
        <h3>Season Totals</h3>
        <SeasonBoxscores seasons={seasons} career={career} />
      </div>
      <div className="summary-section">
        <h3>Season Averages</h3>
        <SeasonBoxscores seasons={seasons} career={career} average={true} />
      </div>
    </div>
  );
};

PlayerViewBoxscore.propTypes = {
  player: PropTypes.object.isRequired,
};

export default PlayerViewBoxscore;
